/**
 * Playbook editor — drag-and-drop ordered list of activities,
 * each expandable to configure via ActivityConfigForm.
 */
import { useState, useCallback } from 'react'
import { useTranslation } from '../i18n/LanguageContext'
import { professional as api, type PlaybookData, type PlaybookItemData } from '../api/client'
import { ACTIVITY_DESCRIPTORS, playbookItemConfigSchema, type ActivitySlug, type PlaybookItemConfig } from './activityConfig'
import ActivityConfigForm from './ActivityConfigForm'

// Derived from ACTIVITY_DESCRIPTORS — adding a new descriptor automatically exposes it here
const ALL_ACTIVITIES = Object.keys(ACTIVITY_DESCRIPTORS) as ActivitySlug[]

const ACTIVITY_EMOJI: Record<ActivitySlug, string> = {
  memory: '🃏', wordmatch: '🔤', cutlery: '🍴', wordsearch: '🔍',
  sudoku: '🔢', maths: '➕', shoppinglist: '🛒', eurmoney: '💶', pillbox: '💊',
  attention: '👁️', symmetry: '🔳', whats_missing: '❓', roman: '🏛️',
  crosswords: '📝', hours: '🕐', synonyms: '↔️', puzzle: '🧩', chess: '♟', numtarget: '🎯',
}

interface Props {
  playbook: PlaybookData
  onBack:   () => void
  onSaved:  (updated: PlaybookData) => void
}

export default function PlaybookEditor({ playbook, onBack, onSaved }: Props) {
  const { t, language } = useTranslation()
  const p = t.professional

  const [items, setItems]             = useState<PlaybookItemData[]>(playbook.items)
  const [expandedId, setExpandedId]   = useState<string | null>(null)
  const [saving, setSaving]           = useState(false)
  const [dragIdx, setDragIdx]         = useState<number | null>(null)
  const [showPicker, setShowPicker]   = useState(false)

  // ── Item config update (local only, saved on "Save changes") ──

  const updateConfig = useCallback((itemId: string, config: PlaybookItemConfig) => {
    setItems(prev => prev.map(it => it.id === itemId ? { ...it, config: config as unknown as Record<string, unknown> } : it))
  }, [])

  // ── Drag-and-drop reorder ─────────────────────────────────────

  const onDragStart = (e: React.DragEvent, idx: number) => {
    setDragIdx(idx)
    e.dataTransfer.effectAllowed = 'move'
  }

  const onDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault()
    if (dragIdx === null || dragIdx === idx) return
    setItems(prev => {
      const next = [...prev]
      const [moved] = next.splice(dragIdx, 1)
      next.splice(idx, 0, moved)
      setDragIdx(idx)
      return next
    })
  }

  const onDragEnd = () => setDragIdx(null)

  // ── Add activity ──────────────────────────────────────────────

  const addActivity = async (slug: ActivitySlug) => {
    const descriptor = ACTIVITY_DESCRIPTORS[slug]
    try {
      const item = await api.addItem(playbook.id, {
        activitySlug: slug,
        config: descriptor.defaultConfig,
      })
      setItems(prev => [...prev, item])
      setExpandedId(item.id)
    } catch {
      // ignore
    }
    setShowPicker(false)
  }

  // ── Remove activity ───────────────────────────────────────────

  const removeItem = async (itemId: string) => {
    await api.deleteItem(itemId).catch(() => {})
    setItems(prev => prev.filter(it => it.id !== itemId))
    if (expandedId === itemId) setExpandedId(null)
  }

  // ── Save (reorder + config updates) ──────────────────────────

  const save = async () => {
    setSaving(true)
    try {
      if (items.length > 0) {
        // 1. Reorder
        await api.reorderItems(playbook.id, items.map(it => it.id))
        // 2. Config updates for each item
        await Promise.all(items.map(it => api.updateItem(it.id, it.config as Record<string, unknown>)))
      }
      onSaved({ ...playbook, items })
    } catch {
      // ignore
    } finally {
      setSaving(false)
    }
  }

  const activityLabel = (slug: string) => {
    const key = slug as ActivitySlug
    if (language === 'es') return t.dashboard.games[key as keyof typeof t.dashboard.games]?.label ?? slug
    return t.dashboard.games[key as keyof typeof t.dashboard.games]?.label ?? slug
  }

  return (
    <div style={s.root}>
      <div style={s.inner}>

        {/* Header */}
        <button onClick={onBack} style={s.backBtn}>{p.back}</button>
        <h1 style={s.title}>{p.editorTitle}: {playbook.name}</h1>

        {/* Item list */}
        {items.length === 0 && (
          <p style={s.empty}>{p.noItems}</p>
        )}

        {items.map((item, idx) => {
          const slug = item.activitySlug as ActivitySlug
          const descriptor = ACTIVITY_DESCRIPTORS[slug]
          const isExpanded = expandedId === item.id

          return (
            <div
              key={item.id}
              draggable
              onDragStart={e => onDragStart(e, idx)}
              onDragOver={e => onDragOver(e, idx)}
              onDragEnd={onDragEnd}
              style={{ ...s.card, ...(dragIdx === idx ? s.cardDragging : {}) }}
            >
              <div style={s.cardHeader}>
                <span style={s.dragHandle} aria-label="Reorder">⠿</span>
                <span style={s.activityEmoji}>{ACTIVITY_EMOJI[slug] ?? '🎮'}</span>
                <span style={s.activityName}>{activityLabel(slug)}</span>
                <div style={s.cardActions}>
                  <button
                    style={s.iconBtn}
                    onClick={() => setExpandedId(isExpanded ? null : item.id)}
                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                  >
                    {isExpanded ? '▲' : '▼'}
                  </button>
                  <button
                    style={{ ...s.iconBtn, ...s.removeBtn }}
                    onClick={() => removeItem(item.id)}
                    aria-label="Remove"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {isExpanded && descriptor && (
                <div style={s.configArea}>
                  <ActivityConfigForm
                    descriptor={descriptor}
                    value={playbookItemConfigSchema.parse(item.config)}
                    onChange={(cfg) => updateConfig(item.id, cfg)}
                  />
                </div>
              )}
            </div>
          )
        })}

        {/* Add activity */}
        {showPicker ? (
          <div style={s.picker}>
            <div style={s.pickerHeader}>
              <span style={s.pickerTitle}>{p.addActivity}</span>
              <button style={s.iconBtn} onClick={() => setShowPicker(false)}>✕</button>
            </div>
            <div style={s.pickerGrid}>
              {ALL_ACTIVITIES.map(slug => (
                <button
                  key={slug}
                  style={s.pickerBtn}
                  onClick={() => addActivity(slug)}
                >
                  <span style={s.pickerEmoji}>{ACTIVITY_EMOJI[slug]}</span>
                  <span style={s.pickerLabel}>{activityLabel(slug)}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <button style={s.addBtn} onClick={() => setShowPicker(true)}>
            + {p.addActivity}
          </button>
        )}

        {/* Save */}
        <button
          style={{ ...s.saveBtn, ...(saving ? s.saveBtnDisabled : {}) }}
          onClick={save}
          disabled={saving}
        >
          {saving ? '…' : p.saveChanges}
        </button>

      </div>
    </div>
  )
}

const s: Record<string, React.CSSProperties> = {
  root: {
    minHeight:      '100vh',
    background:     '#FAF6EF',
    padding:        '0 0 40px',
  },
  inner: {
    maxWidth:   '640px',
    margin:     '0 auto',
    padding:    '24px 16px',
    display:    'flex',
    flexDirection: 'column',
    gap:        '16px',
  },
  backBtn: {
    background:  'none',
    border:      'none',
    color:       '#5B8A5F',
    fontSize: '1rem',
    cursor:      'pointer',
    padding:     '0',
    fontWeight:  600,
    alignSelf:   'flex-start',
    minHeight:   '48px',
  },
  title: {
    fontSize: '1.375rem',
    fontWeight: 700,
    color:      '#2D3A2E',
    margin:     0,
  },
  empty: {
    fontSize: '1rem',
    color:    '#888',
    textAlign: 'center',
    padding:  '32px 0',
  },
  card: {
    background:   '#fff',
    borderRadius: '16px',
    border:       '1px solid #e8e0d5',
    overflow:     'hidden',
    cursor:       'grab',
  },
  cardDragging: {
    opacity:   0.6,
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  },
  cardHeader: {
    display:    'flex',
    alignItems: 'center',
    gap:        '10px',
    padding:    '14px 16px',
  },
  dragHandle: {
    fontSize: '1.25rem',
    color:     '#bbb',
    cursor:    'grab',
    flexShrink: 0,
  },
  activityEmoji: {
    fontSize: '1.375rem',
    flexShrink: 0,
  },
  activityName: {
    fontSize: '1.0625rem',
    fontWeight: 600,
    color:      '#2D3A2E',
    flex:       1,
  },
  cardActions: {
    display: 'flex',
    gap:     '6px',
  },
  iconBtn: {
    background:   'none',
    border:       '1px solid #ddd',
    borderRadius: '8px',
    padding:      '6px 10px',
    minHeight:    '36px',
    cursor:       'pointer',
    fontSize: '0.875rem',
    color:        '#666',
  },
  removeBtn: {
    color:       '#c0392b',
    borderColor: '#f5c6c2',
  },
  configArea: {
    padding:    '0 16px 16px',
    borderTop:  '1px solid #f0ece6',
    paddingTop: '16px',
  },
  addBtn: {
    background:   '#EAF3EB',
    border:       '2px dashed #5B8A5F',
    borderRadius: '14px',
    padding:      '14px',
    fontSize: '1.0625rem',
    fontWeight:   600,
    color:        '#2D5A32',
    cursor:       'pointer',
    minHeight:    '56px',
  },
  picker: {
    background:   '#fff',
    borderRadius: '16px',
    border:       '1px solid #e8e0d5',
    padding:      '16px',
  },
  pickerHeader: {
    display:        'flex',
    justifyContent: 'space-between',
    alignItems:     'center',
    marginBottom:   '12px',
  },
  pickerTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color:      '#2D3A2E',
  },
  pickerGrid: {
    display:             'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap:                 '8px',
  },
  pickerBtn: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    gap:            '4px',
    padding:        '12px 8px',
    minHeight:      '72px',
    background:     '#FAF6EF',
    border:         '1px solid #e8e0d5',
    borderRadius:   '12px',
    cursor:         'pointer',
  },
  pickerEmoji: {
    fontSize: '1.5rem',
  },
  pickerLabel: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color:      '#444',
    textAlign:  'center',
  },
  saveBtn: {
    background:   '#5B8A5F',
    color:        '#fff',
    border:       'none',
    borderRadius: '14px',
    padding:      '16px',
    fontSize: '1.125rem',
    fontWeight:   700,
    cursor:       'pointer',
    minHeight:    '56px',
    marginTop:    '8px',
  },
  saveBtnDisabled: {
    opacity: 0.6,
    cursor:  'not-allowed',
  },
}
